import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Download, ExternalLink, FileText, Minus, Plus, Printer, X } from "lucide-react";
import { PdfView } from "./PdfView";
import { downloadBlob, openInNewTab, printBlob } from "./fileActions";

const IconBtn = ({ onClick, label, testid, children, className = "" }) => (
  <button onClick={onClick} aria-label={label} title={label} data-testid={testid}
    className={`h-9 w-9 inline-flex items-center justify-center rounded-sm text-slate-200 hover:bg-slate-800 shrink-0 ${className}`}>
    {children}
  </button>
);

export const isPdfFile = (filename, contentType) =>
  (contentType || "").includes("pdf") || /\.pdf$/i.test(filename || "");

// Reusable full-screen viewer for PDF (with search) and image files. `file` = { blob, filename, contentType }.
export function DocumentViewer({ file, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [pageInfo, setPageInfo] = useState(null);
  const isPdf = file && isPdfFile(file.filename, file.contentType);
  const url = useMemo(() => (file ? URL.createObjectURL(file.blob) : null), [file]);
  useEffect(() => () => { if (url) setTimeout(() => URL.revokeObjectURL(url), 60000); }, [url]);

  useEffect(() => {
    if (!file) return;
    setZoom(1); setPageInfo(null);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [file, onClose]);

  const onPageInfo = useCallback((cur, total) => setPageInfo({ cur, total }), []);
  if (!file) return null;
  const z = (d) => setZoom((v) => Math.min(4, Math.max(0.5, +(v + d).toFixed(2))));

  return createPortal(
    <div className="fixed inset-0 z-[200] flex flex-col bg-slate-950 text-slate-100" role="dialog" aria-modal="true" data-testid="document-viewer">
      <div className="flex items-center gap-1 px-2 h-12 border-b border-slate-800 bg-slate-900 shrink-0" data-testid="document-viewer-toolbar">
        <FileText className="w-4 h-4 text-slate-400 shrink-0 ml-1" />
        <span className="text-xs sm:text-sm truncate flex-1 min-w-0 ml-1" title={file.filename} data-testid="document-viewer-filename">{file.filename}</span>
        {isPdf && pageInfo && <span className="text-[11px] font-mono text-slate-400 px-1 shrink-0" data-testid="document-viewer-page-info">{pageInfo.cur}/{pageInfo.total}</span>}
        <IconBtn onClick={() => z(-0.25)} label="Uzaklaştır" testid="document-viewer-zoom-out"><Minus className="w-4 h-4" /></IconBtn>
        <span className="hidden sm:inline text-[11px] font-mono w-10 text-center" data-testid="document-viewer-zoom-level">{Math.round(zoom * 100)}%</span>
        <IconBtn onClick={() => z(0.25)} label="Yakınlaştır" testid="document-viewer-zoom-in"><Plus className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={() => downloadBlob(url, file.filename)} label="İndir" testid="document-viewer-download"><Download className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={() => printBlob(url, isPdf)} label="Yazdır" testid="document-viewer-print"><Printer className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={() => openInNewTab(url)} label="Yeni sekmede aç" testid="document-viewer-open-tab" className="hidden sm:inline-flex"><ExternalLink className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={onClose} label="Kapat" testid="document-viewer-close"><X className="w-5 h-5" /></IconBtn>
      </div>
      {isPdf ? <PdfView blob={file.blob} zoom={zoom} onPageInfo={onPageInfo} /> : (
        <div className="flex-1 overflow-auto bg-slate-800/60 p-2" data-testid="document-viewer-image-container">
          <img src={url} alt={file.filename} className="mx-auto max-w-none" style={{ width: `${zoom * 100}%` }} data-testid="document-viewer-image" />
        </div>
      )}
    </div>,
    document.body,
  );
}
